import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", studio: "", phone: "", email: "", password: "", account_type: "studio" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const nav = useNavigate();
  const { refresh } = useAuth();

  async function onSubmit(e) {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error(t("auth.consent_required"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      if (data.status === "pending") {
        setDone(data.message || t("auth.request_received_default"));
        toast.success(t("auth.request_sent"));
        return;
      }
      // Legacy fallback if the API ever returns a token directly
      if (data.access_token) localStorage.setItem("ah_token", data.access_token);
      await refresh();
      toast.success(t("auth.welcome_archihub", { name: data.name }));
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("auth.register_error"));
    } finally {
      setLoading(false);
    }
  }

  const studioLabel =
    form.account_type === "trader" ? t("auth.company")
    : form.account_type === "real_estate" ? t("auth.agency")
    : t("auth.studio");

  return (
    <div className="min-h-[100svh] pt-32 pb-20 bg-cream" data-testid="register-page">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("auth.register_section")}</div>
          <h1 className="mt-4 font-display text-4xl lg:text-5xl font-light text-ink">{t("auth.register_title")}</h1>
          <p className="mt-6 text-slate1 leading-relaxed">
            {t("auth.register_sub")}
          </p>
        </div>

        {done ? (
          <div className="bg-white border border-ink/10 p-8 md:p-10 rounded-sm" data-testid="register-pending">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gold">{t("auth.request_received_title")}</div>
            <h2 className="mt-3 font-display text-3xl text-ink font-light">{t("auth.request_received_h2")}</h2>
            <p className="mt-4 text-slate1 leading-relaxed">{done}</p>
            <p className="mt-2 text-slate1 text-sm">{t("auth.request_received_eta")}</p>
            <Link to="/" className="mt-8 inline-block bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm" data-testid="register-pending-home">{t("auth.request_received_home")}</Link>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="bg-white border border-ink/10 p-8 md:p-10 rounded-sm" data-testid="register-form">
          <div className="mb-6">
            <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2">{t("auth.account_type")}</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup">
              {[
                { v: "studio", t: t("auth.account_studio_t"), d: t("auth.account_studio_d") },
                { v: "trader", t: t("auth.account_trader_t"), d: t("auth.account_trader_d") },
                { v: "real_estate", t: t("auth.account_real_estate_t"), d: t("auth.account_real_estate_d") },
              ].map((opt) => {
                const selected = form.account_type === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm({ ...form, account_type: opt.v })}
                    aria-pressed={selected}
                    className={`text-left p-4 rounded-sm border transition-colors ${selected ? "border-ink bg-ink text-cream" : "border-ink/20 bg-cream hover:border-ink/40"}`}
                    data-testid={`register-account-${opt.v}`}
                  >
                    <div className="text-[11px] tracking-[0.2em] uppercase">{opt.t}</div>
                    <div className={`text-xs mt-1 ${selected ? "text-cream/70" : "text-slate1"}`}>{opt.d}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.name")}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full border border-ink/20 px-4 py-3 bg-cream focus:outline-none focus:border-ink rounded-sm" data-testid="register-name" />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1">
                {studioLabel}
              </label>
              <input value={form.studio} onChange={(e) => setForm({ ...form, studio: e.target.value })}
                className="mt-2 w-full border border-ink/20 px-4 py-3 bg-cream focus:outline-none focus:border-ink rounded-sm" data-testid="register-studio" />
            </div>
          </div>

          <label className="mt-5 block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.phone")}</label>
          <input required type="tel" inputMode="tel" pattern="[\d\s\+\-\(\)\.]{6,}" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+39 ..."
            className="mt-2 w-full border border-ink/20 px-4 py-3 bg-cream focus:outline-none focus:border-ink rounded-sm" data-testid="register-phone" />

          <label className="mt-5 block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.email")}</label>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full border border-ink/20 px-4 py-3 bg-cream focus:outline-none focus:border-ink rounded-sm" data-testid="register-email" />

          <label className="mt-5 block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.password")}</label>
          <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-2 w-full border border-ink/20 px-4 py-3 bg-cream focus:outline-none focus:border-ink rounded-sm" data-testid="register-password" />

          <label className="mt-6 flex items-start gap-3 cursor-pointer text-xs text-slate1 leading-relaxed" data-testid="register-consent-label">
            <input
              type="checkbox"
              required
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-ink shrink-0"
              data-testid="register-consent"
            />
            <span>
              <Trans
                i18nKey="auth.consent_label"
                components={{
                  1: <Link to="/privacy" target="_blank" className="text-ink underline" />,
                  2: <Link to="/termini-condizioni" target="_blank" className="text-ink underline" />,
                }}
              />
            </span>
          </label>

          <button type="submit" disabled={loading}
            className="mt-8 w-full bg-ink text-cream py-4 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-60" data-testid="register-submit">
            {loading ? t("auth.register_btn_loading") : t("auth.register_btn")}
          </button>

          <div className="mt-6 text-sm text-slate1 text-center">
            {t("auth.have_account")} <Link to="/login" className="text-ink underline">{t("auth.login_btn")}</Link>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
